import { useCallback, useState } from "react";

/**
 * The three answers `/buy` already took, handed across in sessionStorage.
 *
 * A visitor who fills in the short form on `public/buy.html` gives their first
 * name, email and phone — all three required there — and then meets the funnel's
 * closing block asking for first name, email and phone again. They have answered
 * every other question by that point, so the repeat lands at the moment of
 * greatest sunk cost.
 *
 * ── WHY SESSIONSTORAGE AND NOT THE `blt` TOKEN ──────────────────────────
 *
 * 🔴 The obvious fix — resolve the continuation token to the lead it was minted
 * for and read the contact off that — is the one thing this must NOT do.
 * `usePrequalBuyLandingToken` spells out why: the token rides in the URL, so it
 * is in browser history, in access logs, and in the `page_view` URLs the SPA
 * shell's gtag reports. That is survivable ONLY because the token "attaches
 * NOTHING without an email that matches the lead it was minted for" — a leaked
 * token buys an attacker an attach they can only complete by already knowing the
 * victim's email. An endpoint that RETURNS that email removes exactly the
 * property the risk assessment rests on, and that comment carries a red warning
 * against weakening it.
 *
 * `/buy` and the funnel are the same origin, so the values cross without a
 * request, without a token, and without ever entering a URL. Nothing new is
 * disclosed anywhere: the data never leaves the browser it was typed into, which
 * makes this strictly safer than the parameter already being carried.
 *
 * ── SCOPE ────────────────────────────────────────────────────────────────
 *
 * Per-tab by construction, so it does not survive a device hop. That is the
 * correct scope rather than a limitation: this exists for the visitor who
 * presses Continue straight after submitting, and they are the entire population
 * that meets the duplicate questions. Someone resuming on another device is
 * already past the point this addresses.
 *
 * Read ONCE into state, never on every render, so a later write by anything else
 * cannot change what the funnel prefills mid-session. Never throws and never
 * blocks: a private window, cleared site data or a malformed blob all degrade to
 * "no prefill", which is exactly today's behaviour.
 *
 * ── LIFETIME ─────────────────────────────────────────────────────────────
 *
 * NOT cleared on read. The visitor may refresh the funnel, and losing the
 * prefill to a reload would hand back the retyping this exists to remove. Every
 * consumer fills blank fields only, so a blob left in place for the run can
 * never overwrite an answer that has since been typed.
 *
 * Cleared when the run ENDS — on finalize, and on Start over. By then it has
 * served its whole purpose, and keeping it costs two things. It leaves contact
 * details sitting in storage for as long as the tab is open, which on a shared
 * or kiosk machine is a long time after the person who typed them walked away.
 * And it can REPLAY: the next person to start a funnel in that tab would be
 * prefilled with the last one's name, email and phone.
 *
 * `forget()` drops the stored blob AND the resolved value, because the two would
 * otherwise disagree — Start over blanks the form while the phone field went on
 * describing a number that had just been cleared.
 */
export const BUY_HANDOFF_STORAGE_KEY = "phx:prequal:buy-handoff:v1";

export interface BuyLandingPrefill {
  firstName: string;
  email: string;
  /** Ten digits, already normalised by `/buy` to match `normalizePhoneDigits`. */
  phone: string;
  /** True when a phone actually came across — the funnel's phone field stops
   *  calling itself optional only then. `/buy` requires both the number and SMS
   *  consent on the same step, so a phone here means both were given; an empty
   *  one means the hand-off is partial and the field keeps its usual copy. */
  hasPhone: boolean;
}

function readOnce(): BuyLandingPrefill | null {
  try {
    const raw = window.sessionStorage.getItem(BUY_HANDOFF_STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;

    const blob = parsed as Record<string, unknown>;
    const str = (v: unknown) => (typeof v === "string" ? v.trim() : "");

    const firstName = str(blob.firstName);
    const email = str(blob.email);
    const phone = str(blob.phone);

    // A blob with nothing usable in it is the same as no blob: returning an
    // all-empty prefill would make `hasPhone` the only thing distinguishing
    // them, which is a distinction no caller wants to reason about.
    if (!firstName && !email && !phone) return null;

    return { firstName, email, phone, hasPhone: phone.length > 0 };
  } catch {
    return null;
  }
}

function clearStored(): void {
  try {
    window.sessionStorage.removeItem(BUY_HANDOFF_STORAGE_KEY);
  } catch {
    // Same posture as the read. Nothing to escalate and nothing to retry: the
    // blob is per-tab, so a clear that could not run is still bounded by the
    // tab's own lifetime rather than left indefinitely.
  }
}

/**
 * The hand-off values, resolved once per mount, and a way to forget them.
 *
 * Shaped like `useState` deliberately: the caller reads the value, and calls the
 * second element when the run ends. See LIFETIME above for when that is.
 */
export function useBuyLandingPrefill(): [BuyLandingPrefill | null, () => void] {
  const [prefill, setPrefill] = useState<BuyLandingPrefill | null>(readOnce);

  const forget = useCallback(() => {
    clearStored();
    setPrefill(null);
  }, []);

  return [prefill, forget];
}
